import { useMemo, useState } from "react";
import {
  MoodActionType,
  useCreateMoodStatFunctionRequest,
  useDeleteMoodStatFunctionRequest,
  useGetMoodStatFunctionRequest,
  useGetStreakFunctionRequest,
  UserMoodStatValue,
  UserStreakValue,
} from "shared/request/myHealthyAdvantageApi";
import "shared/extensions/date";
import { useTranslation } from "react-i18next";
import { formatDate, getCurrentWeekDates } from "core/helpers/dates";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { Mood } from "UIPalette/MoodTrackerCard/moodFactories";
import { useOptimistic } from "../../../hooks/useOptimistic/useOptimistic";
import { useModalContext } from "shared/core/state/ModalContext/ModalContext";
import Modals from "core/enums/modals.enum";
import { useAppReviewContext } from "../AppReviewContext/AppReviewContext";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";

export const useMoodTrackerState = () => {
  const clientCode = ClientInfoState.getCode();
  const { t } = useTranslation();
  const { from, to } = getCurrentWeekDates();
  const today = formatDate(new Date());
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [showMoodJourneyModal, setShowMoodJourneyModal] = useState(false);
  const [moodAction, setMoodAction] = useState<MoodActionType | null>(null);
  const [errorDisplay, setErrorDisplay] = useState<string | null>(null);
  const [triggerMutate, setTriggerMutate] = useState(false);

  const { handlePositiveMoment } = useAppReviewContext();
  const { show: showMoodSupportModal } = useModalContext(Modals.MoodSupport);

  const {
    data: moodStatsRaw,
    isLoading: isGetMoodStatLoading,
    error: getMoodStatError,
    mutate: mutateGetMoodStat,
  } = useGetMoodStatFunctionRequest({ xClientCode: clientCode, from, to });
  const {
    data: streakDataRaw,
    isLoading: isGetStreakLoading,
    error: getStreakError,
    mutate: mutateGetStreak,
  } = useGetStreakFunctionRequest({ xClientCode: clientCode });
  const { trigger: setMoodValueTrigger } = useCreateMoodStatFunctionRequest({ xClientCode: clientCode });
  const { trigger: deleteTrackerValueTrigger } = useDeleteMoodStatFunctionRequest({ xClientCode: clientCode });

  const [moodStats, applyMoodStatOptimistic, resetMoodStatOptimistic, finishMoodStatOptimistic] = useOptimistic<
    UserMoodStatValue | undefined,
    Mood | null
  >(moodStatsRaw, (prev, newMood) => {
    if (prev) {
      let updatedMoods = prev.moods.filter((m) => formatDate(new Date(m.createdDateUtc)) !== today);

      if (newMood !== null) {
        updatedMoods = [
          ...updatedMoods,
          {
            userId: "",
            id: "",
            createdDateUtc: new Date().toISOString(),
            mood: newMood,
          },
        ];
      }
      return { ...prev, moods: updatedMoods };
    }
  });

  const [streakData, applyStreakOptimistic, resetStreakOptimistic, finishStreakOptimistic] = useOptimistic<UserStreakValue | undefined, number>(
    streakDataRaw,
    (prev, days) => {
      if (prev) {
        return { ...prev, days: prev.days + days };
      }
    }
  );

  const currentMood: Mood | undefined = useMemo(
    () => moodStats?.moods.find((mood) => formatDate(new Date(mood.createdDateUtc)) === formatDate(new Date()))?.mood,
    [moodStats]
  );

  const refreshMoodValues = () => {
    mutateGetMoodStat();
    mutateGetStreak();
  };

  const finishOptimistic = () => {
    finishStreakOptimistic();
    finishMoodStatOptimistic();
  };

  const handleMoodAction = (action: MoodActionType): boolean => {
    let delayExperienceModal = false;

    setMoodAction(action);

    switch (action) {
      case MoodActionType.LowMoodSupport:
        trackEvents(EventNames.MOOD_MODAL_VIEWED, {
          modal: "support",
          action: MoodActionType[action],
        });

        showMoodSupportModal();
        break;

      case MoodActionType.LowPositiveYouPrompt:
      case MoodActionType.MidPositiveYouPrompt:
      case MoodActionType.GoodPositiveYouPrompt:
        trackEvents(EventNames.MOOD_MODAL_VIEWED, {
          modal: "journey",
          action: MoodActionType[action],
        });

        setShowMoodJourneyModal(true);
        break;

      case MoodActionType.FirstMoodLogEver:
      case MoodActionType.WelcomeBack:
        trackEvents(EventNames.MOOD_MODAL_VIEWED, {
          modal: "streak",
          action: MoodActionType[action],
        });

        delayExperienceModal = true;
        setShowStreakModal(true);
        break;
    }

    return delayExperienceModal;
  };
  
  async function clearCurrentMoodValue() {
    applyMoodStatOptimistic(null);
    applyStreakOptimistic(-1);

    try {
      await deleteTrackerValueTrigger({});
      refreshMoodValues();
      setTriggerMutate(true);
      finishOptimistic();
    } catch {
      resetMoodStatOptimistic();
      resetStreakOptimistic();
      setErrorDisplay(t("mood-insights.clearMoodError"));
    }
  }

  async function setCurrentMoodValue(mood: Mood) {
    const currentValueHasChanged = currentMood !== mood;

    if (!currentValueHasChanged) return;

    applyMoodStatOptimistic(mood);
    applyStreakOptimistic(1);

    try {
      const response = await setMoodValueTrigger({
        body: {
          mood,
        },
      });

      const action = response?.data?.action;

      if (action !== undefined && action !== MoodActionType.NoAction) {
        const delayExperienceModal = handleMoodAction(action);

        await mutateGetMoodStat();
        await mutateGetStreak();

        if (mood > Mood.Neutral) {
          handlePositiveMoment(delayExperienceModal);
        }
      }
      setTriggerMutate(true);
      finishOptimistic();
    } catch {
      resetMoodStatOptimistic();
      resetStreakOptimistic();
      setErrorDisplay(t("mood-insights.setMoodError"));
    }
  }
  return {
    moodStats,
    streakData,
    moodAction,
    currentMood,
    refreshMoodValues,
    hasError: !!getMoodStatError || !!getStreakError,
    isLoading: isGetMoodStatLoading || isGetStreakLoading,
    setCurrentMoodValue,
    clearCurrentMoodValue,
    showStreakModal,
    showMoodJourneyModal,
    errorDisplay,
    triggerMutate,
    resetTriggerMutate: () => setTriggerMutate(false),
    handleErrorDisplay: (error: string | null) => setErrorDisplay(error),
    handleCloseStreakModal: () => setShowStreakModal(false),
    handleCloseMoodJourneyModal: () => setShowMoodJourneyModal(false),
  };
};
